/**
 * LeaderboardWidget
 * 
 * Embeddable leaderboard component showing yearly rankings.
 * Used in SalesRepPage and can be embedded anywhere.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useGamification } from './GamificationContext';
import { 
  getLeaderboard, 
  getUserLevel,
  awardBonusPoints
} from '../../utils/gamificationUtils';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { hasPermission, FEATURE_PERMISSIONS } from '../../utils/permissions';
import { collection, getDocs } from 'firebase/firestore';
import Tooltip from '../common/Tooltip';

const LeaderboardWidget = ({ 
  maxEntries = 15, 
  showRewardButton = true,
  compact = false 
}) => {
  const { currentUser } = useAuth();
  const { profile: myProfile, currentLevel } = useGamification();
  const [leaderboard, setLeaderboard] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [rewardModal, setRewardModal] = useState({ show: false, user: null });
  
  // Can award bonus points: must be able to modify data AND view all tasks (managers/admins)
  const isManager = hasPermission(currentUser, FEATURE_PERMISSIONS.TASK_VIEW_ALL);
  
  // Available years (current year and past 2 years)
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2];
  }, []);
  
  // Load users for name mapping
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersSnap = await getDocs(collection(db, collectionPath('users')));
        setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error('Failed to load users:', error);
      }
    };
    loadUsers();
  }, []);
  
  // Load leaderboard
  useEffect(() => {
    const loadLeaderboard = async () => {
      setLoading(true);
      try {
        const data = await getLeaderboard('year', maxEntries, selectedYear);
        setLeaderboard(data);
      } catch (error) {
        console.error('Failed to load leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };
    loadLeaderboard();
  }, [selectedYear, maxEntries]);
  
  // Map user IDs to names
  const usersById = useMemo(() => 
    new Map(users.map(u => [u.id, u])), 
    [users]
  );

  // Set of user IDs hidden from reports/achievements
  const hiddenUserIds = useMemo(() => 
    new Set(users.filter(u => u.hideFromReports).map(u => u.id)),
    [users]
  );
  
  // Filter leaderboard to exclude hidden users
  const visibleLeaderboard = useMemo(() => 
    leaderboard.filter(entry => !hiddenUserIds.has(entry.userId)),
    [leaderboard, hiddenUserIds]
  );

  // Find my rank
  const myRank = useMemo(() => 
    visibleLeaderboard.findIndex(entry => entry.userId === currentUser?.uid) + 1,
    [visibleLeaderboard, currentUser?.uid]
  );
  
  const handleReward = async (targetUser, points, reason) => {
    try {
      await awardBonusPoints(currentUser?.uid, targetUser.userId, points, reason, selectedYear);
      setRewardModal({ show: false, user: null });
      // Refresh leaderboard
      const data = await getLeaderboard('year', maxEntries, selectedYear);
      setLeaderboard(data);
    } catch (err) {
      console.error('Failed to award points:', err);
    }
  };
  
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <span className="text-2xl">🏆</span>
            <h3 className="text-lg font-bold">Team Leaderboard</h3>
          </div>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="bg-white/20 text-white border border-white/30 rounded-lg px-3 py-1 text-sm font-medium focus:ring-2 focus:ring-white/50"
          >
            {availableYears.map(year => (
              <option key={year} value={year} className="text-gray-800">
                {year}
              </option>
            ))}
          </select>
        </div>
        
        {/* My Position */}
        {myProfile && selectedYear === new Date().getFullYear() && (
          <div className="mt-3 flex items-center justify-between text-white/90 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-lg">{currentLevel?.icon}</span>
              <span>Your Rank: <strong className="text-white">{myRank > 0 ? `#${myRank}` : '—'}</strong></span>
            </div>
            <div>
              <span className="font-semibold">{(myProfile.yearlyPoints?.[selectedYear] || myProfile.totalPoints || 0).toLocaleString()}</span> pts
            </div>
          </div>
        )}
      </div>
      
      {/* Leaderboard List */}
      <div className={compact ? 'max-h-[400px] overflow-y-auto' : ''}>
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            Loading leaderboard...
          </div>
        ) : visibleLeaderboard.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No data for {selectedYear}. Start earning points!
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {visibleLeaderboard.map((entry, index) => {
              const user = usersById.get(entry.userId);
              const yearPoints = entry.yearlyPoints?.[selectedYear] || entry.totalPoints || 0;
              const level = getUserLevel(yearPoints);
              const isMe = entry.userId === currentUser?.uid;
              
              return (
                <div 
                  key={entry.id}
                  className={`flex items-center gap-3 p-3 transition-colors ${
                    isMe ? 'bg-amber-50' : 'hover:bg-gray-50'
                  }`}
                >
                  {/* Rank */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    index === 0 ? 'bg-yellow-400 text-yellow-900' :
                    index === 1 ? 'bg-gray-300 text-gray-700' :
                    index === 2 ? 'bg-amber-600 text-amber-100' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {index + 1}
                  </div>
                  
                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-base">{level.icon}</span>
                      <span className="font-medium text-gray-900 truncate text-sm">
                        {user?.displayName || 'Unknown'}
                        {isMe && <span className="text-amber-600 ml-1">(You)</span>}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Lvl {level.level} • {level.name}
                    </div>
                  </div>
                  
                  {/* Stats (hide on compact) */}
                  {!compact && (
                    <div className="hidden sm:flex items-center gap-3 text-xs text-gray-500">
                      <Tooltip text="Deals Won">
                        <span>💰 {entry.stats?.dealsWon || 0}</span>
                      </Tooltip>
                      <Tooltip text="Tasks Completed">
                        <span>✅ {entry.stats?.tasksCompleted || 0}</span>
                      </Tooltip>
                      <Tooltip text="Current Streak">
                        <span>🔥 {entry.currentStreak || 0}</span>
                      </Tooltip>
                    </div>
                  )}
                  
                  {/* Points */}
                  <div className="text-right">
                    <div className="font-bold text-sm text-gray-900">
                      {yearPoints.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">pts</div>
                  </div>
                  
                  {/* Manager Reward Button */}
                  {showRewardButton && isManager && !isMe && (
                    <Tooltip text="Award bonus points">
                      <button
                        onClick={() => setRewardModal({ show: true, user: { ...entry, displayName: user?.displayName } })}
                        className="p-1.5 text-amber-600 hover:bg-amber-100 rounded-full transition-colors"
                      >
                        🎁
                      </button>
                    </Tooltip>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Manager Reward Modal */}
      {rewardModal.show && (
        <RewardModal 
          user={rewardModal.user}
          year={selectedYear}
          onClose={() => setRewardModal({ show: false, user: null })}
          onSubmit={(points, reason) => handleReward(rewardModal.user, points, reason)}
        />
      )}
    </div>
  );
};

// Reward Modal Component
const RewardModal = ({ user, year, onClose, onSubmit }) => {
  const [points, setPoints] = useState(50);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const presetReasons = [
    'Outstanding customer service',
    'Exceeded sales target',
    'Great teamwork',
    'Creative problem solving',
    'Going above and beyond'
  ];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Please provide a reason');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      await onSubmit(points, reason);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">
              🎁 Reward {user.displayName}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          
          <div className="mb-4 text-sm text-gray-600">
            Points will be added to <strong>{year}</strong> totals
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Points to Award
              </label>
              <div className="flex gap-2 flex-wrap">
                {[25, 50, 100, 250, 500].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPoints(p)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      points === p
                        ? 'bg-amber-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    +{p}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quick Reasons
              </label>
              <div className="flex flex-wrap gap-2">
                {presetReasons.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setReason(r)}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${
                      reason === r
                        ? 'bg-amber-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Award *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                rows={2}
                placeholder="Why are you recognizing this person?"
              />
            </div>
            
            {error && <div className="text-red-600 text-sm">{error}</div>}
            
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold hover:from-amber-600 hover:to-orange-600 disabled:opacity-50"
              >
                {saving ? 'Awarding...' : `Award +${points} Points`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardWidget;
